import { z } from "zod"

// fd-boutique-13: индивидуальный заказ с витрины магазина.
//
// Покупатель проходит мастер на витрине бутика (тип товара → описание →
// размеры и параметры → фото-референсы → бюджет и срок → тип оплаты →
// контакты), заявка уходит продавцу тредом в чатах, а дальше работает уже
// знакомый контур: продавец присылает смету, покупатель принимает её в корзину,
// и заказ из сметы оформляется как индивидуальный.

/** Способ оплаты, который покупатель выбирает в мастере: те же, что в оформлении. */
export const ShopCustomOrderPaymentTypeSchema = z.enum(["card", "invoice"])
export type ShopCustomOrderPaymentType = z.infer<typeof ShopCustomOrderPaymentTypeSchema>
export const SHOP_CUSTOM_ORDER_PAYMENT_TYPES: ShopCustomOrderPaymentType[] = ["card", "invoice"]

/** Строка прайса продавца: тип изделия и цена «от» за штуку (руб.). */
export const ShopCustomOrderPriceItemSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().trim().min(1).max(80),
  price: z.number().int().min(0).max(100_000_000).optional(),
})
export type ShopCustomOrderPriceItem = z.infer<typeof ShopCustomOrderPriceItemSchema>

/** Настройки индивидуального заказа в конструкторе бутика (`config.customOrder`). */
export const ShopCustomOrderSettingsSchema = z.object({
  productTypes: z.array(ShopCustomOrderPriceItemSchema).max(30),
  paymentTypes: z.array(ShopCustomOrderPaymentTypeSchema).max(2),
  /** Наценка за срочность, %: заказ к сроку раньше `leadDays` дней. */
  urgentMarkupPercent: z.number().int().min(0).max(300).optional(),
  /** Обычный срок изготовления, дней. */
  leadDays: z.number().int().min(1).max(365).optional(),
  note: z.string().max(500).optional(),
})
export type ShopCustomOrderSettings = z.infer<typeof ShopCustomOrderSettingsSchema>

export const ShopCustomOrderContactMethodSchema = z.enum(["chat", "phone"])
export type ShopCustomOrderContactMethod = z.infer<typeof ShopCustomOrderContactMethodSchema>

/** Тело POST /shops/:slug/custom-orders. */
export const CreateShopCustomOrderSchema = z.object({
  /** id строки прайса продавца; без него — свой тип («Другое»). */
  productTypeId: z.string().max(64).optional(),
  productType: z.string().trim().min(1).max(80),
  description: z.string().trim().min(2).max(2000),
  quantity: z.number().int().min(1).max(1000).default(1),
  size: z.string().trim().max(120).optional(),
  parameters: z.string().trim().max(1000).optional(),
  photos: z.array(z.string().min(1).max(1000)).max(6).default([]),
  budget: z.number().int().min(0).max(100_000_000).optional(),
  /** Желаемый срок, ГГГГ-ММ-ДД. */
  deadline: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  paymentType: ShopCustomOrderPaymentTypeSchema,
  contactName: z.string().trim().min(1).max(120),
  contactPhone: z.string().trim().max(40).optional(),
  contactMethod: ShopCustomOrderContactMethodSchema.default("chat"),
})
export type CreateShopCustomOrder = z.input<typeof CreateShopCustomOrderSchema>

/**
 * Статус заявки для покупателя — выводится из треда:
 * sent — отправлена, продавец ещё не ответил; in_progress — продавец ответил
 * в чате; offer — пришла смета; accepted — смета принята (заказ оформляется);
 * cancelled — покупатель отменил заявку.
 */
export const ShopCustomOrderStatusSchema = z.enum([
  "sent",
  "in_progress",
  "offer",
  "accepted",
  "cancelled",
])
export type ShopCustomOrderStatus = z.infer<typeof ShopCustomOrderStatusSchema>

export const ShopCustomOrderSchema = z.object({
  id: z.string(),
  shopId: z.string(),
  shopSlug: z.string().optional(),
  shopName: z.string().optional(),
  conversationId: z.string(),
  productType: z.string(),
  description: z.string(),
  quantity: z.number(),
  size: z.string().optional(),
  parameters: z.string().optional(),
  photos: z.array(z.string()),
  budget: z.number().optional(),
  deadline: z.string().optional(),
  paymentType: ShopCustomOrderPaymentTypeSchema,
  contactName: z.string(),
  contactPhone: z.string().optional(),
  contactMethod: ShopCustomOrderContactMethodSchema,
  /** Предварительная цена по прайсу продавца, руб.; нет прайса — null. */
  estimatePrice: z.number().nullable(),
  status: ShopCustomOrderStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type ShopCustomOrder = z.infer<typeof ShopCustomOrderSchema>

export const ShopCustomOrderListSchema = z.object({ items: z.array(ShopCustomOrderSchema) })
export type ShopCustomOrderList = z.infer<typeof ShopCustomOrderListSchema>

const DAY_MS = 86_400_000

/**
 * Предварительная цена по прайсу продавца: «цена от» × количество, плюс
 * наценка за срочность, если срок раньше обычного срока изготовления.
 * Одна функция на витрину и шлюз — покупатель видит ровно ту цифру, что
 * уйдёт продавцу. Нет прайса или цены у типа — null (расчёта нет).
 */
export const estimateShopCustomOrderPrice = (
  settings: ShopCustomOrderSettings | undefined | null,
  input: { productTypeId?: string; quantity?: number; deadline?: string },
  now: Date = new Date(),
): number | null => {
  if (!settings || !input.productTypeId) return null
  const item = settings.productTypes.find((type) => type.id === input.productTypeId)
  if (!item || item.price == null || item.price <= 0) return null
  const quantity = Math.max(1, Math.floor(input.quantity ?? 1))
  let total = item.price * quantity
  const markup = settings.urgentMarkupPercent ?? 0
  if (markup > 0 && settings.leadDays && input.deadline) {
    const due = Date.parse(`${input.deadline}T00:00:00Z`)
    const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
    if (Number.isFinite(due) && (due - today) / DAY_MS < settings.leadDays) {
      total = total * (1 + markup / 100)
    }
  }
  return Math.round(total)
}
